/**
 * Liquidity / market-cap gate for the provider stock pickers (PWA-2597).
 *
 * Applies ONLY when adding a stock to a basket or a portfolio in the provider
 * experience, and applies there to every user role with no admin bypass -- the
 * platform cannot trade these stocks regardless of who picked them.
 *
 * Kept as a pure module so the same rule can be reused if it is ever needed on
 * the write path (currently client-side only).
 */

/** Thresholds are expected to be tuned -- they are defined here and nowhere else. */
export const MIN_AVG_DOLLAR_VOLUME_USD = 25_000_000;
export const MIN_MARKET_CAP_USD = 3_000_000_000;

export type StockLiquidityStatus = "GREEN" | "YELLOW" | "RED";

export interface StockLiquidityInput {
  secType?: string;
  avgDollarVolume?: number | null;
  marketCapUsd?: number | null;
}

/**
 * Security types the gate does not apply to. Volume and market cap are not
 * meaningful reference data for these instruments.
 *
 * Note ETFs are NOT exempt: they carry secType STK and are gated like any other
 * stock. Anything not listed here (including a missing secType) is gated, so an
 * unrecognised type fails closed rather than slipping past the check.
 */
const EXEMPT_SEC_TYPES = new Set(["OPT", "CASH", "FUND"]);

/**
 * `null` means the figure was never computed; `0` is treated identically as
 * missing per the ticket's rule. `undefined` covers the field being absent from
 * the payload entirely.
 */
const isMissing = (value?: number | null): boolean =>
  value === null || value === undefined || value === 0;

/**
 * `avgDollarVolume` is authoritative whenever it is present. `marketCapUsd` is a
 * fallback proxy consulted only when there is no volume figure at all -- it is
 * never a second chance to pass. A stock measured thin on volume stays RED no
 * matter how large its market cap.
 *
 * Both comparisons are strict `>`: a value exactly at a threshold fails.
 */
export const classifyStockLiquidity = (contract: StockLiquidityInput): StockLiquidityStatus => {
  if (EXEMPT_SEC_TYPES.has((contract.secType ?? "").toUpperCase())) return "GREEN";

  const volumeMissing = isMissing(contract.avgDollarVolume);
  const marketCapMissing = isMissing(contract.marketCapUsd);

  if (volumeMissing && marketCapMissing) return "YELLOW";
  if (!volumeMissing) return contract.avgDollarVolume! > MIN_AVG_DOLLAR_VOLUME_USD ? "GREEN" : "RED";
  return contract.marketCapUsd! > MIN_MARKET_CAP_USD ? "GREEN" : "RED";
};

/** Short status label -- the accessible text alternative to the row icon. */
export const STOCK_LIQUIDITY_LABEL: Record<StockLiquidityStatus, string> = {
  GREEN: "Liquidity check passed",
  YELLOW: "Liquidity data missing",
  RED: "Cannot be traded on this platform",
};

/**
 * Full explanation shown on hover, to screen readers, and in the dialog body.
 * GREEN has no message -- a passing row says nothing beyond its label.
 *
 * The volume figure in the YELLOW copy is derived from the threshold constant
 * so the two cannot drift when it is tuned. Rendered in whole millions on
 * purpose ("$25M") rather than through numberUtils, which would emit "$25.0M".
 * Note this assumes the threshold stays in the millions -- a value of 1e9 would
 * read "$1000M". Revisit the formatting if it is ever tuned that far.
 */
export const STOCK_LIQUIDITY_MESSAGE: Record<StockLiquidityStatus, string> = {
  GREEN: "",
  YELLOW: `Please ensure that this stock meets the minimum volume requirement of $${
    MIN_AVG_DOLLAR_VOLUME_USD / 1_000_000
  }M per day. We do not have this data.`,
  RED: "This stock does not meet the liquidity criteria for traded volume.",
};

export const STOCK_LIQUIDITY_DIALOG_TITLE: Record<StockLiquidityStatus, string> = {
  GREEN: "",
  YELLOW: "Add stock with missing liquidity data?",
  RED: "Stock cannot be added",
};
